import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PersonalInfoStep = ({ formData, onUpdate, onNext }) => {
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(null);

  const countryOptions = [
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'de', label: 'Germany' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const age = new Date()?.getFullYear() - new Date(formData.dateOfBirth)?.getFullYear();
      if (age < 18) {
        newErrors.dateOfBirth = 'You must be at least 18 years old';
      }
    }

    if (!formData?.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData?.address?.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.postalCode?.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const handleChange = (field, value) => {
    onUpdate({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Personal Information
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Please provide your accurate personal details for account verification
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
          <Input
            label="First Name"
            type="text"
            placeholder="Enter your first name"
            value={formData?.firstName || ''}
            onChange={(e) => handleChange('firstName', e?.target?.value)}
            error={errors?.firstName}
            required
          />

          <Input
            label="Last Name"
            type="text"
            placeholder="Enter your last name"
            value={formData?.lastName || ''}
            onChange={(e) => handleChange('lastName', e?.target?.value)}
            error={errors?.lastName}
            required
          />

          <div className="relative">
            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@example.com"
              value={formData?.email || ''}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
            <button
              type="button"
              className="absolute right-2 top-9 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              onMouseEnter={() => setShowTooltip('email')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <Icon name="Info" size={16} />
            </button>
            {showTooltip === 'email' && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-[var(--color-popover)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg z-10">
                <p className="text-xs text-[var(--color-popover-foreground)]">
                  We'll use this email for account verification and important security notifications
                </p>
              </div>
            )}
          </div>

          <Input
            label="Phone Number"
            type="tel"
            placeholder="+1 (555) 000-0000"
            value={formData?.phone || ''}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />

          <Input
            label="Date of Birth"
            type="date"
            value={formData?.dateOfBirth || ''}
            onChange={(e) => handleChange('dateOfBirth', e?.target?.value)}
            error={errors?.dateOfBirth}
            required
          />

          <Select
            label="Country"
            placeholder="Select your country"
            options={countryOptions}
            value={formData?.country || ''}
            onChange={(value) => handleChange('country', value)}
            error={errors?.country}
            required
          />
        </div>
      </div>
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-secondary)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="MapPin" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Address Information
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Your residential address for compliance verification
            </p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5 lg:space-y-6">
          <Input
            label="Street Address"
            type="text"
            placeholder="123 Main Street, Apt 4B"
            value={formData?.address || ''}
            onChange={(e) => handleChange('address', e?.target?.value)}
            error={errors?.address}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
            <Input
              label="City"
              type="text"
              placeholder="Enter your city"
              value={formData?.city || ''}
              onChange={(e) => handleChange('city', e?.target?.value)}
              error={errors?.city}
              required
            />

            <Input
              label="Postal Code"
              type="text"
              placeholder="12345"
              value={formData?.postalCode || ''}
              onChange={(e) => handleChange('postalCode', e?.target?.value)}
              error={errors?.postalCode}
              required
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Identity Verification
        </Button>
      </div>
    </form>
  );
};

export default PersonalInfoStep;